import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Tab,
  Tabs,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Badge,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  Alert
} from '@mui/material';
import ComplianceSettings from '../components/ComplianceSettings';
import CustomPHIPatterns from '../components/CustomPHIPatterns';
import {
  Business as BusinessIcon,
  Group as GroupIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Star as StarIcon,
  Upgrade as UpgradeIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import axios from 'axios';
import {
  Organization,
  OrganizationMember,
  InviteUserRequest,
  UpdateOrganizationRequest,
  CreateOrganizationRequest
} from '../types/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const OrganizationManagement: React.FC = () => {
  const { user } = useAuth();
  const [currentTab, setCurrentTab] = useState(0);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [editOrgDialog, setEditOrgDialog] = useState(false);
  const [inviteDialog, setInviteDialog] = useState(false);
  const [createOrgDialog, setCreateOrgDialog] = useState(false);
  
  // Form states
  const [orgForm, setOrgForm] = useState<UpdateOrganizationRequest>({});
  const [inviteForm, setInviteForm] = useState<InviteUserRequest>({
    email: '',
    role: 'member'
  });
  const [createOrgForm, setCreateOrgForm] = useState<CreateOrganizationRequest>({
    name: '',
    subscription_plan: 'free'
  });

  useEffect(() => {
    fetchOrganizationData();
  }, []);

  const fetchOrganizationData = async () => {
    try {
      setLoading(true);
      
      // Fetch organization details
      const orgResponse = await axios.get('/api/organizations/me');
      if (orgResponse.data.data) {
        setOrganization(orgResponse.data.data);
        setOrgForm({
          name: orgResponse.data.data.name,
          description: orgResponse.data.data.description,
          domain: orgResponse.data.data.domain
        });
        
        // Fetch organization members
        const membersResponse = await axios.get('/api/organizations/me/members');
        console.log('Organization members response:', membersResponse.data);
        setMembers(membersResponse.data.data || []);
      }
    } catch (error: any) {
      if (error.response?.status === 404) {
        // User doesn't have an organization
        setOrganization(null);
      } else {
        toast.error('Failed to fetch organization data');
        console.error('Error fetching organization:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateOrganization = async () => {
    try {
      await axios.put('/api/organizations/me', orgForm);
      toast.success('Organization updated successfully');
      setEditOrgDialog(false);
      fetchOrganizationData();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to update organization');
    }
  };

  const handleCreateOrganization = async () => {
    try {
      await axios.post('/api/organizations', createOrgForm);
      toast.success('Organization created successfully');
      setCreateOrgDialog(false);
      fetchOrganizationData();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to create organization');
    }
  };

  const handleInviteUser = async () => {
    try {
      await axios.post('/api/organizations/me/members', inviteForm);
      toast.success(`Invitation sent to ${inviteForm.email}`);
      setInviteDialog(false);
      setInviteForm({ email: '', role: 'member' });
      fetchOrganizationData();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to invite user');
    }
  };

  const handleRemoveMember = async (userId: number) => {
    if (!window.confirm('Are you sure you want to remove this member?')) {
      return;
    }

    try {
      await axios.delete(`/api/organizations/me/members/${userId}`);
      toast.success('Member removed successfully');
      fetchOrganizationData();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to remove member');
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner': return 'error';
      case 'admin': return 'warning';
      case 'department_head': return 'info';
      case 'team_lead': return 'primary';
      default: return 'default';
    }
  };

  const getSubscriptionIcon = (plan: string) => {
    switch (plan) {
      case 'enterprise': return <StarIcon sx={{ color: 'gold' }} />;
      case 'team': return <UpgradeIcon sx={{ color: 'blue' }} />;
      default: return <CheckCircleIcon sx={{ color: 'green' }} />;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  if (!organization) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <BusinessIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom>
              Create Your Organization
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Get started by creating your organization to collaborate with your team.
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => setCreateOrgDialog(true)}
            >
              Create Organization
            </Button>
          </CardContent>
        </Card>

        {/* Create Organization Dialog */}
        <Dialog open={createOrgDialog} onClose={() => setCreateOrgDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Create Organization</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label="Organization Name"
                value={createOrgForm.name}
                onChange={(e) => setCreateOrgForm({ ...createOrgForm, name: e.target.value })}
                sx={{ mb: 2 }}
                required
              />
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={createOrgForm.description || ''}
                onChange={(e) => setCreateOrgForm({ ...createOrgForm, description: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Domain"
                placeholder="https://example.com"
                value={createOrgForm.domain || ''}
                onChange={(e) => setCreateOrgForm({ ...createOrgForm, domain: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                select
                label="Subscription Plan"
                value={createOrgForm.subscription_plan}
                onChange={(e) => setCreateOrgForm({ ...createOrgForm, subscription_plan: e.target.value as any })}
              >
                <MenuItem value="free">Free (5 users, 3 teams)</MenuItem>
                <MenuItem value="team">Team (25 users, 10 teams)</MenuItem>
                <MenuItem value="enterprise">Enterprise (Unlimited)</MenuItem>
              </TextField>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateOrgDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateOrganization} variant="contained">
              Create
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Organization Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your organization settings, members, and teams.
        </Typography>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <BusinessIcon sx={{ fontSize: 40, color: 'primary.main' }} />
              <Box>
                <Typography variant="h5">{organization.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {organization.description || 'No description'}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {getSubscriptionIcon(organization.subscription_plan)}
              <Chip
                label={organization.subscription_plan.toUpperCase()}
                color={organization.subscription_plan === 'enterprise' ? 'error' : 
                       organization.subscription_plan === 'team' ? 'primary' : 'default'}
                variant="outlined"
              />
            </Box>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <Box sx={{ textAlign: 'center' }}>
                <GroupIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6">{organization.member_count || 0}</Typography>
                <Typography variant="body2" color="text.secondary">Members</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ textAlign: 'center' }}>
                <BusinessIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6">{organization.team_count || 0}</Typography>
                <Typography variant="body2" color="text.secondary">Teams</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Chip
                  icon={<CheckCircleIcon />}
                  label={`${organization.user_role?.toUpperCase()}`}
                  color={getRoleColor(organization.user_role || '')}
                  variant="outlined"
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Your Role
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
            <Tab label="Overview" />
            <Tab label="Members" />
            <Tab label="Settings" />
            <Tab label="Compliance" />
          </Tabs>
        </Box>

        <TabPanel value={currentTab} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>Organization Details</Typography>
              <List>
                <ListItem>
                  <ListItemText 
                    primary="Name" 
                    secondary={organization.name} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Slug" 
                    secondary={organization.slug} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Domain" 
                    secondary={organization.domain || 'Not set'} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Created" 
                    secondary={new Date(organization.created_at).toLocaleDateString()} 
                  />
                </ListItem>
              </List>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>Features</Typography>
              <List>
                <ListItem>
                  <ListItemText 
                    primary="Max Users" 
                    secondary={organization.features?.max_users || organization.max_users} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Max Teams" 
                    secondary={organization.features?.max_teams || organization.max_teams} 
                  />
                </ListItem>
                {/* <ListItem>
                  <ListItemText primary="Advanced Analytics" />
                  <ListItemSecondaryAction>
                    <Switch 
                      checked={organization.features?.advanced_analytics || false} 
                      disabled
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                  <ListItemText primary="Custom Rules" />
                  <ListItemSecondaryAction>
                    <Switch 
                      checked={organization.features?.custom_rules || false} 
                      disabled
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                  <ListItemText primary="Priority Support" />
                  <ListItemSecondaryAction>
                    <Switch 
                      checked={organization.features?.priority_support || false} 
                      disabled
                    />
                  </ListItemSecondaryAction>
                </ListItem> */}
              </List>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">Organization Members</Typography>
            {(organization.user_role === 'owner' || organization.user_role === 'admin') && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setInviteDialog(true)}
              >
                Invite Member
              </Button>
            )}
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Joined</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {members.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon />
                        <Box>
                          <Typography variant="body2">
                            {member.User?.email || 'Unknown User'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {member.User?.job_title || 'No title'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={member.role.replace('_', ' ').toUpperCase()}
                        color={getRoleColor(member.role)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{member.department || 'Not assigned'}</TableCell>
                    <TableCell>
                      {new Date(member.joined_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {(organization.user_role === 'owner' || organization.user_role === 'admin') && 
                       member.user_id !== user?.id && (
                        <Tooltip title="Remove member">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveMember(member.user_id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={currentTab} index={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">Organization Settings</Typography>
            {(organization.user_role === 'owner' || organization.user_role === 'admin') && (
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => setEditOrgDialog(true)}
              >
                Edit Settings
              </Button>
            )}
          </Box>

          <Alert severity="info" sx={{ mb: 3 }}>
            Organization settings can only be modified by owners and administrators.
          </Alert>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>Basic Information</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText primary="Name" secondary={organization.name} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Description" secondary={organization.description || 'Not set'} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Domain" secondary={organization.domain || 'Not set'} />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>Subscription</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    {getSubscriptionIcon(organization.subscription_plan)}
                    <Typography variant="h6">
                      {organization.subscription_plan.charAt(0).toUpperCase() + organization.subscription_plan.slice(1)} Plan
                    </Typography>
                  </Box>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="Users" 
                        secondary={`${organization.member_count}/${organization.max_users === 'unlimited' ? '∞' : organization.max_users}`} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Teams" 
                        secondary={`${organization.team_count}/${organization.max_teams === 'unlimited' ? '∞' : organization.max_teams}`} 
                      />
                    </ListItem>
                  </List>
                  {organization.subscription_plan !== 'enterprise' && (
                    <Button variant="outlined" startIcon={<UpgradeIcon />} sx={{ mt: 2 }}>
                      Upgrade Plan
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={currentTab} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <ComplianceSettings />
            </Grid>
            <Grid item xs={12}>
              <CustomPHIPatterns />
            </Grid>
          </Grid>
        </TabPanel>
      </Card>

      {/* Edit Organization Dialog */}
      <Dialog open={editOrgDialog} onClose={() => setEditOrgDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Organization</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Name"
              value={orgForm.name || ''}
              onChange={(e) => setOrgForm({ ...orgForm, name: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={orgForm.description || ''}
              onChange={(e) => setOrgForm({ ...orgForm, description: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Domain"
              value={orgForm.domain || ''}
              onChange={(e) => setOrgForm({ ...orgForm, domain: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOrgDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdateOrganization} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite User Dialog */}
      <Dialog open={inviteDialog} onClose={() => setInviteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invite Team Member</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={inviteForm.email}
              onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
              sx={{ mb: 2 }}
              required
            />
            <TextField
              fullWidth
              select
              label="Role"
              value={inviteForm.role}
              onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value as any })}
              sx={{ mb: 2 }}
            >
              <MenuItem value="member">Member</MenuItem>
              <MenuItem value="team_lead">Team Lead</MenuItem>
              <MenuItem value="department_head">Department Head</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </TextField>
            <TextField
              fullWidth
              label="Department"
              value={inviteForm.department || ''}
              onChange={(e) => setInviteForm({ ...inviteForm, department: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialog(false)}>Cancel</Button>
          <Button onClick={handleInviteUser} variant="contained">
            Send Invitation
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default OrganizationManagement; 